<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import AmountInput from "../../../../components/ui/form/AmountInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const methodList = [
  { id: "Straighline", name: "Straighline" },
  { id: "Declining", name: "Declining" },
];

const durationTypeList = [
  { id: "Months", name: "Months" },
  { id: "Years", name: "Years" },
];

const computationList = [
  { id: "No Prorata", name: "No Prorata" },
  { id: "Constant Periods", name: "Constant Periods" },
  { id: "Based on Days per Period", name: "Based on Days per Period" },
];

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  name: props.data?.name || props.listCreateName || "",
  method: props.data?.method || "",
  duration: props.data?.duration || "",
  duration_type: props.data?.duration_type || "",
  computation: props.data?.computation || "",
  not_depreciated_value_p: props.data?.not_depreciated_value_p || 0,
  depreciation_accounts_id: props.data?.depreciation_accounts_id || "",
  expense_accounts_id: props.data?.expense_accounts_id || "",
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("name", [required("Name is required")]);
setFieldRules("method", [required("Method is required")]);
setFieldRules("duration", [required("Duration is required")]);
setFieldRules("duration_type", [required("Duration Type is required")]);
setFieldRules("computation", [required("Computation is required")]);
setFieldRules("depreciation_accounts_id", [
  required("Fixed Asset Account is required"),
]);
setFieldRules("expense_accounts_id", [required("Expense Account is required")]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})

</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-3 gap-6">
        <TextInput
          v-model="fields.name.value"
          label="Name"
          placeholder="Enter Name"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <SearchSelect
          v-model="fields.method.value"
          :options="methodList"
          :readonly="isReadOnly"
          label="Method"
          :error="fields.method.touched ? fields.method.error : null"
          @blur="fields.method.touched = true"
        />

        <SearchSelect
          v-model="fields.computation.value"
          :options="computationList"
          :readonly="isReadOnly"
          label="Computation"
          :error="fields.computation.touched ? fields.computation.error : null"
          @blur="fields.computation.touched = true"
        />

        <AmountInput
          v-model="fields.duration.value"
          label="Duration"
          :readonly="isReadOnly"
          :error="fields.duration.touched ? fields.duration.error : null"
          @blur="fields.duration.touched = true"
        />

        <SearchSelect
          v-model="fields.duration_type.value"
          :options="durationTypeList"
          :readonly="isReadOnly"
          label="Duration Type"
          :error="
            fields.duration_type.touched ? fields.duration_type.error : null
          "
          @blur="fields.duration_type.touched = true"
        />

        <AmountInput
          v-model="fields.not_depreciated_value_p.value"
          label="Not Depreciated Value (%)"
          :readonly="isReadOnly"
          :error="
            fields.not_depreciated_value_p.touched
              ? fields.not_depreciated_value_p.error
              : null
          "
          @blur="fields.not_depreciated_value_p.touched = true"
        />

        <SearchSelect
          v-model="fields.depreciation_accounts_id.value"
          label="Depreciation Account"
          :readonly="isReadOnly"
          extra="Expense"
          end-point="finance/accounting/chart-of-accounts"
          :error="
            fields.depreciation_accounts_id.touched
              ? fields.depreciation_accounts_id.error
              : null
          "
          @blur="fields.depreciation_accounts_id.touched = true"
        />

        <SearchSelect
          v-model="fields.expense_accounts_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Expense Account"
          extra="Expense"
          :readonly="isReadOnly"
          :error="
            fields.expense_accounts_id.touched
              ? fields.expense_accounts_id.error
              : null
          "
          @blur="fields.expense_accounts_id.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
